import type {
  ConfigureFamily,
  ConfigureOptionsResponse,
} from "@/api/configure-options";
import {
  catalogItemsForFamily,
  getCustomToolingItem,
} from "@/utils/configure-catalog-display";
import { parseDecimalString } from "@/utils/cost-display";
import {
  filterMdsCatalogItems,
  type MdsFilterState,
} from "@/utils/mds-configure";
import { selectionMapToItems } from "@/utils/configure-storage";

// =============================================================================
// configure-override-sum.ts — pure configured-investment total (the number that
// becomes `machinePriceOverride`). Extracted from compute-configured-investment.ts
// so the POC-parity golden fixtures can assert it without the network/getConfigureOptions
// import. POC reference: `computeConfiguredMachinePriceForApps`.
//
// NOTE: family-specific adders (e.g. the MDS A240 +180,000 robot adder, POC
// `computeConfiguredMachinePriceForApps`) are layered on by the caller via
// `extraAdder` — see docs/spec/configure-auto-select-poc-vs-mvp-gaps.md §4.
// The adder value comes from cost_models.additional_investment exposed on the
// configure-options response; never hardcode the literal.
// =============================================================================

export interface ConfiguredOverrideArgs {
  selection: ReadonlyMap<string, number>;
  response: ConfigureOptionsResponse;
  family: ConfigureFamily;
  profilElementType: string | null;
  mdsFilters: MdsFilterState;
  /** Family adder summed on top of the line total (e.g. MDS A240 robot adder). */
  extraAdder?: number;
  /**
   * When true, per-fastener tooling groups are included in the total.
   * Used by the "Your selected configuration" display total so it matches
   * the configure modal's "Selected options total". The cost-engine override
   * (machinePriceOverride) always excludes tooling per POC parity.
   */
  includeTooling?: boolean;
}

export function configuredOverrideTotal(args: ConfiguredOverrideArgs): number {
  const { selection, response, family, profilElementType, mdsFilters } = args;

  const customToolingItem = getCustomToolingItem();
  let catalogItems = catalogItemsForFamily(response.catalogItems, family);
  if (family === "profil_indi" && profilElementType) {
    catalogItems = catalogItems.filter((i) => i.elementType === profilElementType);
  }
  if (family === "mds") {
    catalogItems = filterMdsCatalogItems(catalogItems, mdsFilters);
  }

  const priceByPartNo = new Map<string, number>();
  const addPrice = (partNo: string, price: string | null) => {
    const n = parseDecimalString(price);
    if (n != null && n >= 0) priceByPartNo.set(partNo, n);
  };
  for (const item of catalogItems) addPrice(item.partNo, item.price);
  addPrice(customToolingItem.partNo, customToolingItem.price);
  if (response.customTooling) {
    addPrice(response.customTooling.partNo, response.customTooling.price);
  }

  // POC parity (verified by live override capture 2026-06-08): per-fastener
  // tooling (`response.toolingGroups`, e.g. the H-131-10L MANUAL tool) is
  // auto-selected for the QUOTE but EXCLUDED from the machine-price total
  // sent to the cost engine (`machinePriceOverride`). POC's
  // `computeConfiguredMachinePriceForApps` sums `getSelectedConfigItemsWithQty`,
  // which filters against `getConfigItemsWithCustomTooling()` =
  // `getAvailableConfigItems()` + custom tooling — the per-fastener tooling groups
  // are NEVER in that list, so they never enter the price. MVP previously priced
  // them, making every Heger override exactly +€334.94 vs POC. So we neither price
  // these part-nos nor sum them here — UNLESS `includeTooling` is set (used for the
  // display-only "Your selected configuration" total that must match the
  // configure modal's "Selected options total").
  const toolingPartNos = new Set<string>();
  if (!args.includeTooling) {
    for (const group of response.toolingGroups ?? []) {
      for (const item of group.items) toolingPartNos.add(item.partNo);
    }
  }
  for (const group of response.toolingGroups ?? []) {
    for (const item of group.items) addPrice(item.partNo, item.price);
  }

  const items = selectionMapToItems(selection, priceByPartNo);
  let acc = 0;
  for (const it of items) {
    if (toolingPartNos.has(it.partNo)) continue;
    const unit = it.price ?? priceByPartNo.get(it.partNo);
    if (unit == null || unit <= 0) continue;
    acc += unit * it.qty;
  }

  const adder = args.extraAdder;
  if (adder != null && Number.isFinite(adder) && adder > 0) acc += adder;
  return acc;
}
